// frontend/src/hooks/useMe.ts — boot-essentials user payload.
//
// `['me']` is the ONLY query key carrying the full freshness ladder per
// CONVENTIONS §Frontend state + ADR-003 §Revocation propagation:
//
//   Layer 1: staleTime 60s + refetchOnWindowFocus true
//            (tab-return triggers an up-to-60s-stale refresh; covers the
//            "I came back to my desk" case).
//   Layer 2: refetchInterval 60s + refetchIntervalInBackground false
//            (the actor's tab quietly polls every 60s while focused;
//            background tabs sleep — no battery drain on hidden tabs).
//   Layer 3: BroadcastChannel `auth` — same-browser cross-tab cascade.
//            See `lib/authBroadcast.ts` + `RootLayout`'s AppSideEffects
//            listener.
//
// Selectors per call-site (NOT a shared selector library) — referentially
// stable projections via top-level `const`s defined in the consumer file.

import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import type { MeResponse } from '@ems-app/schemas/auth/responses';
import { apiClient } from '../lib/apiClient.js';

export const ME_QUERY_KEY = ['me'] as const;

// The session's own check — the tab's question, never the person's act: a
// BUSY answer raises no toast (ADR-079 §5); TanStack re-asks on its rhythm.
async function fetchMe(): Promise<MeResponse> {
  const r = await apiClient.get<MeResponse>('/auth/me', { quietBusy: true });
  return r.data;
}

// Overload: with selector → returns the projected type; without → returns full MeResponse.
export function useMe(): UseQueryResult<MeResponse, Error>;
export function useMe<TSelected>(
  select: (me: MeResponse) => TSelected,
): UseQueryResult<TSelected, Error>;
export function useMe<TSelected = MeResponse>(
  select?: (me: MeResponse) => TSelected,
): UseQueryResult<TSelected, Error> {
  const options: UseQueryOptions<MeResponse, Error, TSelected> = {
    queryKey: ME_QUERY_KEY,
    queryFn: fetchMe,
    // Layer 1
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    // Layer 2
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  };
  if (select) options.select = select;
  return useQuery(options);
}
